// Palvelinyhteys. Osoite ja API-avain tulevat asetuksista, jotta sama
// sovellus toimii kehityskoneen, Raspberry Pin ja pilvipalvelimen kanssa.
//
// Oletusosoite:
//   - kun käyttöliittymä tarjoillaan samalta palvelimelta: sama origin
//   - Viten kehityspalvelimessa: http://<sama kone>:8000
//   - Capacitor-sovelluksessa osoite asetetaan Asetuksista

import { storage } from "./storage.js";
import { playerKey, rememberPlayerKey } from "./playerKeys.js";
import { canActAs, serverIdentity, sessionHeaders } from './accountSession.js';

/** Kyselyparametrit merkkijonoksi: tyhjät arvot jätetään pois, taulukot erotetaan pilkulla. */
export function query(params = {}) {
  const parts = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && v.length === 0))
    .map(([k, v]) => `${k}=${encodeURIComponent(Array.isArray(v) ? v.join(",") : v)}`);
  return parts.length ? `?${parts.join("&")}` : "";
}

export class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.status = status;
  }
}

function defaultBaseUrl() {
  if (typeof window === "undefined") return "http://localhost:8000";
  // Puhelinsovelluksessa sivu tulee laitteelta itseltään, joten palvelimen
  // osoite on pakko asettaa. Tyhjä arvo näyttää käyttäjälle asetusohjeen.
  if (isNativeApp()) return "";
  const { protocol, hostname, port } = window.location;
  if (port === "5173" || port === "4173") return `${protocol}//${hostname}:8000`;
  return window.location.origin; // käyttöliittymä ja API samalta palvelimelta
}

/** Palvelimen virhe, jonka syy on puuttuva tai vanhentunut pelaaja-avain (ei ylläpitoavain tms.). */
export function isKeyProblem(detail) {
  return typeof detail === "string" && detail.startsWith("Pelaajan avain");
}

export function isNativeApp() {
  return typeof window !== "undefined" && Boolean(window.Capacitor?.isNativePlatform?.());
}

/**
 * Julkaisu-APK:n mukaan leivotut oletukset (build_release.ps1 antaa VITE_SERVER_URL ja VITE_API_KEY).
 * Näin läheisten sovellus toimii heti asennuksen jälkeen, eikä mitään tarvitse kirjoittaa.
 * `env` annetaan parametrina, jotta testit eivät riipu Viten rakennusajan arvoista.
 */
export function builtInServer(env = import.meta.env) {
  return { baseUrl: String(env?.VITE_SERVER_URL ?? "").replace(/\/+$/, ""), apiKey: String(env?.VITE_API_KEY ?? "") };
}

/**
 * Palvelinasetukset: käyttäjän Asetuksissa tallentama arvo ensin, tyhjän kentän tilalla
 * APK:n sisäänleivottu oletus ja viimeisenä ympäristön oletus.
 */
export function resolveServerSettings(stored, builtIn, fallbackBaseUrl) {
  return {
    baseUrl: stored?.baseUrl || builtIn.baseUrl || fallbackBaseUrl,
    apiKey: stored?.apiKey || builtIn.apiKey || "",
  };
}

export function getServerSettings() {
  return resolveServerSettings(storage.get("server", null), builtInServer(), defaultBaseUrl());
}

export function setServerSettings(settings) {
  storage.set("server", settings);
}

/**
 * Luo API-asiakkaan. fetchImpl voidaan vaihtaa testeissä.
 * Jokainen virhe muutetaan ApiErroriksi, jonka viesti on näytettävissä.
 */
export function createApi({
  fetchImpl = (...a) => fetch(...a),
  settings = getServerSettings,
  // Pelaaja, jonka puolesta kutsut tehdään. Oletus on käyttäjän valitsema pelaaja;
  // `api.as(id)` antaa asiakkaan, joka puhuu aina tietyn pelaajan puolesta (esim.
  // lähetysjono: retki lähetetään sen pelaajan tietoihin, joka sen tallensi).
  player = () => storage.get("player", null),
} = {}) {
  async function request(method, path, body, { asText = false, admin = false, adminKey, retried = false } = {}) {
    const { baseUrl, apiKey } = settings();
    if (baseUrl === "" && isNativeApp()) {
      throw new ApiError("Palvelimen osoite puuttuu. Lisää se Passin asetuksista.", 0);
    }
    let response;
    const who = player();
    // Ylläpitoavain lähetetään aina, kun se on tallennettu tähän puhelimeen (vain ylläpitäjän puhelimessa): se avaa
    // kaikkien pelaajien tiedot ja ylläpitotoiminnot. Tavallinen puhelin ei koskaan lähetä sitä.
    const adminHeader = adminKey ?? storage.get("adminKey", "");
    // Pelaajan oma avain: palvelin antaa sen pelaajaa luotaessa ja sovellus lähettää sen itse.
    const token = who ? playerKey(who) : "";
    const accountHeaders = sessionHeaders(serverIdentity({ baseUrl }));
    const controller = new AbortController();
    const deadline = setTimeout(() => controller.abort(), path.startsWith('/imports') ? 120000 : 25000);
    try {
      response = await fetchImpl(`${baseUrl}/api${path}`, {
        method,
        credentials: 'include',
        signal: controller.signal,
        headers: {
          ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
          ...(apiKey ? { "X-Api-Key": apiKey } : {}),
          ...(adminHeader && !accountHeaders.Authorization && !accountHeaders['X-CSRF-Token'] ? { "X-Admin-Key": adminHeader } : {}),
          ...accountHeaders,
          ...(isNativeApp() ? { 'X-Native-Client': '1' } : {}),
          // Omistaja on oletus, joten otsake jätetään pois: vanha palvelinkin toimii.
          ...(who && (who !== "me" || Object.keys(accountHeaders).length) ? { "X-Player": who } : {}),
          ...(token ? { "X-Player-Token": token } : {}),
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
    } catch {
      throw new ApiError("Palvelimeen ei saada yhteyttä. Retki tallentuu puhelimeen ja lähetetään myöhemmin.", 0);
    } finally {
      clearTimeout(deadline);
    }
    if (!response.ok) {
      let detail = `Palvelin vastasi virheellä ${response.status}.`;
      try {
        const data = await response.json();
        if (typeof data.detail === "string") detail = data.detail;
      } catch {
        /* vastaus ei ollut JSONia */
      }
      // Avain puuttuu tai on vanhentunut (uudelleenasennus, avain annettu uudelleen): haetaan uusi ja yritetään kerran.
      if (response.status === 403 && who && !retried && !accountHeaders['X-CSRF-Token'] && isKeyProblem(detail) && (await recoverKey(who))) {
        return request(method, path, body, { asText, admin, adminKey, retried: true });
      }
      throw new ApiError(detail, response.status);
    }
    if (asText) return response.text();
    if (response.status === 204) return null;
    try {
      return await response.json();
    } catch {
      // Esim. väärä osoite tai vanha palvelinversio palauttaa HTML-sivun.
      throw new ApiError(`Palvelin ei tunnistanut toimintoa ${path}. Päivitä palvelin uusimpaan versioon.`, response.status);
    }
  }

  /** Hakee pelaajalle uuden avaimen (lukitsematon vanha pelaaja tai sama puhelin uudelleenasennuksen jälkeen). */
  async function claim(id) {
    const { getDeviceId } = await import("./deviceId.js");
    const { token } = await request("POST", `/players/${id}/claim`, { device: await getDeviceId() }, { retried: true });
    rememberPlayerKey(id, token);
    return true;
  }

  const recovering = new Map(); // pelaaja -> käynnissä oleva haku, jotta rinnakkaiset pyynnöt eivät hae montaa avainta
  function recoverKey(id) {
    if (!recovering.has(id)) {
      recovering.set(id, claim(id).catch(() => false).finally(() => recovering.delete(id)));
    }
    return recovering.get(id);
  }

  return {
    accountMe: () => request('GET', '/me'),
    canActAs: (id) => canActAs(id || 'me', serverIdentity(settings())),
    registerAccount: (body) => request('POST', '/auth/register', body),
    loginAccount: (body) => request('POST', '/auth/login', body),
    logoutAccount: () => request('POST', '/auth/logout'),
    recoverAccount: (body) => request('POST', '/auth/recover', body),
    recoveryEmail: (email) => request('POST', '/auth/recovery-email', {email}),
    teams: () => request('GET', '/teams'),
    team: (id) => request('GET', `/teams/${encodeURIComponent(id)}`),
    createTeam: (body) => request('POST', '/teams', body),
    inviteTeam: (id) => request('POST', `/teams/${encodeURIComponent(id)}/invites`),
    revokeTeamInvites: (id) => request('DELETE', `/teams/${encodeURIComponent(id)}/invites`),
    previewTeamInvite: (code) => request('GET', `/team-invites/${encodeURIComponent(code)}`),
    acceptTeamInvite: (code, replace) => request('POST', `/team-invites/${encodeURIComponent(code)}/accept`, {replace}),
    leaveTeam: (id) => request('DELETE', `/teams/${encodeURIComponent(id)}/membership`),
    removeTeamMember: (id, player) => request('DELETE', `/teams/${encodeURIComponent(id)}/members/${encodeURIComponent(player)}`),
    transferTeam: (id, player_id) => request('POST', `/teams/${encodeURIComponent(id)}/ownership-transfer`, {player_id}),
    adminAddTeamMember: (id, body) => request('POST', `/admin/teams/${encodeURIComponent(id)}/members`, body),
    playerClaimCode: (player_id) => request('POST', '/admin/player-claims', {player_id}),
    adminRemoveTeamMember: (id, player) => request('DELETE', `/admin/teams/${encodeURIComponent(id)}/members/${encodeURIComponent(player)}`),
    teamLeaderboard: (period) => request('GET', `/leaderboard${query({kind:'teams', period})}`),
    as: (id) => createApi({ fetchImpl, settings, player: () => id }),
    players: () => request("GET", "/players"),
    // Palvelin antaa uudelle pelaajalle avaimen vain tässä vastauksessa: se tallennetaan heti eikä jää kutsujan vastuulle.
    createPlayer: async (body) => {
      const { token, ...saved } = await request("POST", "/players", body);
      rememberPlayerKey(saved.id, token);
      return saved;
    },
    /** Pelaajan avaimen lunastus: lukitsee vanhan pelaajan (luotu ennen avaimia) tai palauttaa avaimen samalle puhelimelle. */
    claimPlayer: (id) => claim(id),
    // Puhelimen tunnisteella aiemmin luodut pelaajat (uusi asennus tai tyhjentynyt muisti löytää ne takaisin).
    playersMine: (device) => request("GET", `/players/mine?device=${encodeURIComponent(device)}`),
    updatePlayer: (id, body) => request("PATCH", `/players/${id}`, body),
    deletePlayer: (id) => request("DELETE", `/players/${id}`),
    // Ylläpitäjä: siirtää pelaajan toisen pelaajan perheeseen ({ join_with }) tai omaan uuteen perheeseen ({ new_family: true }).
    setPlayerFamily: (id, body) => request("PUT", `/admin/players/${id}/family`, body, { admin: true }),
    family: () => request("GET", "/family"),
    /** Pelaajien sijoitukset: period = all | week | month. */
    /** Edellisen viikon ja kuukauden voittajat (ilmoitus ja kortin rivi). */
    leaderboardWinners: () => request("GET", "/leaderboard/winners"),
    leaderboard: (period) => request("GET", `/leaderboard${query({ period })}`),
    familyTiles: () => request("GET", "/family/tiles"),
    setCompanions: (tripId, companions) => request("PUT", `/trips/${tripId}/companions`, { companions }),
    /** Kolme seikkailukorttia sijainnista. companions: kaverien tunnisteet, seed: uusi ehdotus. */
    adventures: ({ lat, lon, seed, companions }) => request("GET", `/adventures${query({ lat, lon, seed, with: companions })}`),
    completeAdventure: (body) => request("POST", "/adventures/complete", body),
    adventureLog: () => request("GET", "/adventures/log"),
    health: () => request("GET", "/health"),
    config: () => request("GET", "/config"),
    stats: () => request("GET", "/stats"),
    // until: aikakone (Unix-sekunteja), modes: kulkutapasuodatus (taulukko tunnisteita)
    tiles: (until, modes) => request("GET", `/tiles${query({ until, modes })}`),
    shapes: (until, modes) => request("GET", `/tiles/shapes${query({ until, modes })}`),
    heatmap: ({ south, west, north, east }, zoom, modes, until) =>
      request("GET", `/heatmap${query({ south, west, north, east, zoom, modes, until })}`),
    impossible: () => request("GET", "/impossible"),
    markImpossible: (key, note) => request("POST", "/impossible", { key, note: note || null }, { admin: true }),
    unmarkImpossible: (key) => request("DELETE", `/impossible?key=${encodeURIComponent(key)}`, undefined, { admin: true }),
    /** Keräämättömien ruutujen vienti. params: kind, level, lat, lon, limit, modes */
    exportTiles: (format, params) => request("GET", `/export/tiles.${format}${query(params)}`, undefined, { asText: true }),
    content: () => request("GET", "/content"),
    trips: () => request("GET", "/trips"),
    trip: (id) => request("GET", `/trips/${id}`),
    ledger: () => request("GET", "/ledger"),
    createTrip: (trip) => request("POST", "/trips", Object.keys(sessionHeaders(serverIdentity(settings()))).length ? { ...trip, companions: [] } : trip),
    addPoints: (id, points) => request("POST", `/trips/${id}/points`, { points }),
    changeMode: (id, start_t, mode) => request("POST", `/trips/${id}/mode`, { start_t, mode }),
    replaceModes: (id, spans) => request("PUT", `/trips/${id}/modes`, { spans }),
    pause: (id, start_t, end_t) => request("POST", `/trips/${id}/pause`, { start_t, end_t }),
    finishTrip: (id, ended_at) => request("POST", `/trips/${id}/finish`, { ended_at }),
    renameTrip: (id, name) => request("PATCH", `/trips/${id}`, { name }),
    deleteTrip: (id) => request("DELETE", `/trips/${id}`),
    confirm: (body) => request("POST", "/confirmations", body),
    revoke: (id) => request("DELETE", `/confirmations/${id}`),
    exportBackup: () => request("GET", "/backup"),
    exportGpx: (tripId) => request("GET", tripId ? `/export/trips.gpx?trip_id=${tripId}` : "/export/trips.gpx", undefined, { asText: true }),
    exportLedgerCsv: () => request("GET", "/export/ledger.csv", undefined, { asText: true }),
    exportTripsCsv: () => request("GET", "/export/trips.csv", undefined, { asText: true }),
    setPoiLocation: (id, body) => request("PUT", `/content/pois/${id}/location`, body, { admin: true }),
    setRouteGeometry: (id, body) => request("PUT", `/content/routes/${id}/geometry`, body, { admin: true }),
    routeFromTrip: (id, body) => request("POST", `/content/routes/${id}/geometry-from-trip`, body, { admin: true }),
    mapLayers: () => request("GET", "/maptiles"),
    achievements: () => request("GET", "/achievements"),
    previewImport: (filename, content_b64) => request("POST", "/imports/preview", { filename, content_b64 }),
    importTracks: (filename, content_b64, tracks, companions = []) => request("POST", "/imports", { filename, content_b64, tracks, companions }),
    weekly: () => request("GET", "/goals/week"),
    setWeeklyGoal: (goal) => request("PUT", "/goals/week", { goal }),
    yearSummary: (year) => request("GET", year ? `/summary/year?year=${year}` : "/summary/year"),
    base: () => request("GET", "/base"),
    build: (id) => request("POST", "/base/buildings", { id }),
    setDecor: (id, visible) => request("PATCH", `/base/decor/${id}`, { visible }),
    wishlists: () => request("GET", "/wishlists"),
    createWishlist: (name) => request("POST", "/wishlists", { name }),
    renameWishlist: (id, name) => request("PATCH", `/wishlists/${id}`, { name }),
    deleteWishlist: (id) => request("DELETE", `/wishlists/${id}`),
    addWish: (listId, key) => request("POST", `/wishlists/${listId}/items`, { key }),
    removeWish: (listId, key) => request("DELETE", `/wishlists/${listId}/items?key=${encodeURIComponent(key)}`),
    /** Lähellä olevien salaisten kohteiden vihjeet: vain vihje ja epätarkka rengas, ei paikkaa. */
    hiddenNearby: (lat, lon) => request("GET", `/hidden/nearby${query({ lat, lon })}`),
    // Ylläpito (X-Admin-Key). adminStatus tarkistaa annetun avaimen ennen kuin se tallennetaan.
    adminStatus: (key) => request("GET", "/admin/status", undefined, { admin: true, adminKey: key }),
    adminBackup: () => request("GET", "/admin/backup", undefined, { admin: true }),
    adminContent: () => request("GET", "/admin/content", undefined, { admin: true }),
    adminContentReload: () => request("POST", "/admin/content/reload", undefined, { admin: true }),
    // Reitti GPX:stä: esikatselu ei tallenna mitään; tallennus luo reitin tai korvaa olemassa olevan reitin linjan.
    adminRoutePreview: (filename, content_b64) => request("POST", "/admin/routes/preview", { filename, content_b64 }, { admin: true }),
    adminRouteSave: (body) => request("POST", "/admin/routes", body, { admin: true }),
    // Julkaistu sovellusversio ({ build, name, size }; build 0 = ei julkaistu). Päivitysilmoitus (lib/appUpdate.js).
    appVersion: () => request("GET", "/app-version"),
    // Uusi kohde paikan päällä: piste GPS:stä, palvelin kirjoittaa kohteen lisäpakettiin (admin-pois.json).
    adminPoiCreate: (body) => request("POST", "/admin/pois", body, { admin: true }),
    adminHidden: () => request("GET", "/admin/hidden", undefined, { admin: true }),
    saveHidden: (id, body) => (id ? request("PUT", `/admin/hidden/${id}`, body, { admin: true }) : request("POST", "/admin/hidden", body, { admin: true })),
    deleteHidden: (id) => request("DELETE", `/admin/hidden/${id}`, undefined, { admin: true }),
    importBackup: (data, mode) => request("POST", `/backup?mode=${mode}`, data),
  };
}

export const api = createApi();
